import React from 'react';
import { Pagination, PaginationItem } from '@mui/material';
import type { PaginationProps } from '@mui/material/Pagination';
import type { SxProps, Theme } from '@mui/material/styles';
import { ReactComponent as ChevronIcon } from '../../static/images/svg/tabler_chevron-down.svg';
import themeTypography from '../../themes/typography';

/** 公共分页组件入参。 */
interface CommonPaginationProps {
    /** 总页数。 */
    count: number;
    /** 当前页码，从 1 开始。 */
    page: number;
    /** 页码切换回调。 */
    onChange: (page: number) => void;
    /** 是否禁用分页操作。 */
    disabled?: boolean;
    /** 分页容器的自定义类名。 */
    className?: string;
    /** MUI 分页组件的无障碍标签与扩展配置。 */
    ariaLabel?: PaginationProps['aria-label'];
}

/** 设计稿中的分页项视觉样式。 */
const paginationItemSx = {
    width: 40,
    height: 40,
    minWidth: 40,
    padding: 1,
    boxSizing: 'border-box',
    border: '1px solid var(--stroke-primary-300)',
    borderRadius: '8px',
    color: 'var(--text-dark)',
    backgroundColor: 'var(--fill-neutral-100)',
    ...themeTypography().body1,
    '&:hover': {
        backgroundColor: 'var(--fill-neutral-100)',
    },
    '&.Mui-selected': {
        color: 'var(--fill-neutral-100)',
        backgroundColor: 'var(--fill-primary-300)',
        '&:hover': {
            backgroundColor: 'var(--fill-primary-300)',
        },
    },
    '&.Mui-disabled': {
        color: 'var(--text-secondary)',
        backgroundColor: 'var(--fill-neutral-200)',
        borderColor: 'var(--stroke-neutral-100)',
    },
} as SxProps<Theme>;

/** 分页上一页箭头图标。 */
function PreviousPageIcon(): React.ReactElement {
    return <ChevronIcon width={24} height={24} style={{ transform: 'rotate(90deg)' }} />;
}

/** 分页下一页箭头图标。 */
function NextPageIcon(): React.ReactElement {
    return <ChevronIcon width={24} height={24} style={{ transform: 'rotate(-90deg)' }} />;
}

/**
 * 将分页项转换为设计稿中的 40px 方形按钮，并保留 MUI 的分页行为。
 * @param item - MUI 生成的分页项参数
 * @returns 设计稿样式的分页项
 */
function renderPaginationItem(item: React.ComponentProps<typeof PaginationItem>) {
    /** 分页方向图标组件映射。 */
    let components: { previous?: typeof PreviousPageIcon; next?: typeof NextPageIcon } | undefined;
    if (item.type === 'previous') {
        components = { previous: PreviousPageIcon };
    } else if (item.type === 'next') {
        components = { next: NextPageIcon };
    }

    return <PaginationItem {...item} components={components} sx={paginationItemSx} />;
}

/**
 * 展示符合设计稿规范的公共分页控件。
 * @param props - 公共分页组件参数
 * @returns 公共分页控件
 */
export default function CommonPagination(
    props: Readonly<CommonPaginationProps>,
): React.ReactElement {
    const { count, page, onChange, disabled = false, className, ariaLabel } = props;

    return (
        <Pagination
            className={className}
            count={count}
            page={page}
            disabled={disabled}
            aria-label={ariaLabel}
            siblingCount={3}
            boundaryCount={1}
            renderItem={renderPaginationItem}
            onChange={(_event, nextPage) => onChange(nextPage)}
            sx={{
                '& .MuiPagination-ul': {
                    gap: 1,
                },
            }}
        />
    );
}
